/**
 * Firestore Database Service
 * Handles database operations for organizations, configurations, and calculations
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  DocumentSnapshot,
  QueryConstraint,
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { logger } from '@/utils/logger';
import {
  Organization,
  OrganizationConfig,
  Calculation,
  SalaryConfig,
  SocialChargeConfig,
  StaffRatioConfig,
  GeneralExpenseConfig,
  OperationalExpenses,
  PositionType,
  RoleDescriptionConfig,
} from '@/types';

// Collection names
export const COLLECTIONS = {
  ORGANIZATIONS: 'organizations',
  CALCULATIONS: 'calculations',
  BASE_CONFIGURATIONS: 'baseConfigurations',
  GLOBAL_PARAMETERS: 'globalParameters',
  PARAMETER_CHANGE_LOGS: 'parameterChangeLogs',
  USERS: 'users',
  REPORTS: 'reports',
} as const;

// Generic CRUD operations
export class FirestoreService<T> {
  constructor(private collectionName: string) {}

  /**
   * Create a new document
   */
  async create(id: string, data: Omit<T, 'id'>): Promise<void> {
    await setDoc(doc(db, this.collectionName, id), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Get a document by ID
   */
  async getById(id: string): Promise<T | null> {
    const docRef = doc(db, this.collectionName, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as T;
    }
    return null;
  }

  /**
   * Update a document
   */
  async update(id: string, data: Partial<T>): Promise<void> {
    await updateDoc(doc(db, this.collectionName, id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Delete a document (soft delete by setting isActive: false)
   */
  async softDelete(id: string): Promise<void> {
    await updateDoc(doc(db, this.collectionName, id), {
      isActive: false,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Hard delete a document
   */
  async delete(id: string): Promise<void> {
    await deleteDoc(doc(db, this.collectionName, id));
  }

  /**
   * Get all documents with optional filters
   */
  async getAll(constraints: QueryConstraint[] = []): Promise<T[]> {
    logger.info(
      `FirestoreService: Starting getAll for collection ${this.collectionName}`,
      {
        constraintsCount: constraints.length,
        firebaseUser: auth.currentUser
          ? {
              uid: auth.currentUser.uid,
              email: auth.currentUser.email,
              tokenExpiry: auth.currentUser.metadata.lastSignInTime,
            }
          : null,
      }
    );

    try {
      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      const results = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as T;
      });

      logger.info(
        `FirestoreService: Successfully retrieved ${results.length} documents from ${this.collectionName}`
      );
      return results;
    } catch (error) {
      logger.error(
        `FirestoreService: Error getting documents from ${this.collectionName}`,
        {
          error: error instanceof Error ? error.message : String(error),
          firebaseUser: auth.currentUser ? { uid: auth.currentUser.uid } : null,
          stack: error instanceof Error ? error.stack : undefined,
        }
      );
      throw error;
    }
  }
}

// Organization service
export const organizationService = new FirestoreService<Organization>(
  COLLECTIONS.ORGANIZATIONS
);

// Extend organization service with custom methods
export const organizationOperations = {
  // Inherit all base methods
  getById: (id: string) => organizationService.getById(id),
  getAll: (constraints?: QueryConstraint[]) =>
    organizationService.getAll(constraints),
  create: (id: string, data: any) => organizationService.create(id, data),
  update: (id: string, data: any) => organizationService.update(id, data),
  delete: (id: string) => organizationService.delete(id),

  /**
   * Get active organizations only
   */
  async getActiveOrganizations(): Promise<Organization[]> {
    try {
      // Try the optimized query with index
      return await organizationService.getAll([
        where('isActive', '==', true),
        orderBy('name'),
      ]);
    } catch (error) {
      console.log('Index not ready, falling back to client-side sorting');
      // Fallback: get all active organizations and sort client-side
      const allOrgs = await organizationService.getAll([
        where('isActive', '==', true),
      ]);
      return allOrgs.sort((a, b) => a.name.localeCompare(b.name));
    }
  },

  /**
   * Create organization with generated ID
   */
  async createOrganization(
    data: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const docRef = doc(collection(db, COLLECTIONS.ORGANIZATIONS));
    await organizationService.create(docRef.id, {
      ...data,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return docRef.id;
  },

  /**
   * Update organization configuration
   */
  async updateConfiguration(
    id: string,
    config: OrganizationConfig
  ): Promise<void> {
    await organizationService.update(id, { customConfigurations: config });
  },
};

// Calculation service
export const calculationService = new FirestoreService<Calculation>(
  COLLECTIONS.CALCULATIONS
);

// Extend calculation service with custom methods
export const calculationOperations = {
  // Inherit all base methods
  getById: (id: string) => calculationService.getById(id),
  getAll: (constraints?: QueryConstraint[]) =>
    calculationService.getAll(constraints),
  create: (id: string, data: any) => calculationService.create(id, data),
  update: (id: string, data: any) => calculationService.update(id, data),
  delete: (id: string) => calculationService.delete(id),

  /**
   * Get calculations for an organization
   */
  async getByOrganization(
    organizationId: string,
    limitCount = 50
  ): Promise<Calculation[]> {
    return calculationService.getAll([
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc'),
      limit(limitCount),
    ]);
  },

  /**
   * Create calculation with generated ID
   */
  async createCalculation(
    data: Omit<Calculation, 'id' | 'createdAt'>
  ): Promise<string> {
    const docRef = doc(collection(db, COLLECTIONS.CALCULATIONS));
    await calculationService.create(docRef.id, {
      ...data,
      createdAt: new Date(),
    });
    return docRef.id;
  },

  /**
   * Get recent calculations across all organizations
   */
  async getRecentCalculations(limitCount = 20): Promise<Calculation[]> {
    return calculationService.getAll([
      orderBy('createdAt', 'desc'),
      limit(limitCount),
    ]);
  },
};

// Base configuration service
export interface BaseConfigurations {
  salaries: Record<PositionType, SalaryConfig>;
  socialCharges: SocialChargeConfig;
  staffRatios: StaffRatioConfig;
  generalExpenses: GeneralExpenseConfig;
  operationalExpenses: OperationalExpenses; // Base operational expenses configuration
  roleDescriptions: Record<PositionType, RoleDescriptionConfig>;
  lastUpdated: Date;
  updatedBy: string;
}

export const baseConfigService = {
  /**
   * Get base configurations
   */
  async getBaseConfigurations(): Promise<BaseConfigurations | null> {
    const docRef = doc(db, COLLECTIONS.BASE_CONFIGURATIONS, 'default');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...data,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
      } as BaseConfigurations;
    }
    return null;
  },

  /**
   * Update base configurations
   */
  async updateBaseConfigurations(
    config: Omit<BaseConfigurations, 'lastUpdated'>,
    updatedBy: string
  ): Promise<void> {
    await setDoc(doc(db, COLLECTIONS.BASE_CONFIGURATIONS, 'default'), {
      ...config,
      lastUpdated: serverTimestamp(),
      updatedBy,
    });
  },

  /**
   * Update salary configuration
   */
  async updateSalaries(
    salaries: Record<PositionType, SalaryConfig>,
    updatedBy: string
  ): Promise<void> {
    const currentConfig = await this.getBaseConfigurations();
    if (currentConfig) {
      await this.updateBaseConfigurations(
        { ...currentConfig, salaries },
        updatedBy
      );
    }
  },

  /**
   * Update social charges configuration
   */
  async updateSocialCharges(
    socialCharges: SocialChargeConfig,
    updatedBy: string
  ): Promise<void> {
    const currentConfig = await this.getBaseConfigurations();
    if (currentConfig) {
      await this.updateBaseConfigurations(
        { ...currentConfig, socialCharges },
        updatedBy
      );
    }
  },

  /**
   * Update staff ratios configuration
   */
  async updateStaffRatios(
    staffRatios: StaffRatioConfig,
    updatedBy: string
  ): Promise<void> {
    const currentConfig = await this.getBaseConfigurations();
    if (currentConfig) {
      await this.updateBaseConfigurations(
        { ...currentConfig, staffRatios },
        updatedBy
      );
    }
  },
};

// Pagination helper
export interface PaginatedResult<T> {
  data: T[];
  nextCursor: DocumentSnapshot | null;
  hasMore: boolean;
}

/**
 * Get paginated results
 */
export const getPaginated = async <T>(
  collectionName: string,
  constraints: QueryConstraint[],
  pageSize = 20,
  cursor?: DocumentSnapshot
): Promise<PaginatedResult<T>> => {
  const queryConstraints = [...constraints, limit(pageSize + 1)];
  if (cursor) {
    queryConstraints.push(startAfter(cursor));
  }

  const q = query(collection(db, collectionName), ...queryConstraints);
  const querySnapshot = await getDocs(q);

  const docs = querySnapshot.docs;
  const hasMore = docs.length > pageSize;
  const data = docs.slice(0, pageSize);

  return {
    data: data.map(doc => {
      const docData = doc.data();
      return {
        id: doc.id,
        ...docData,
        createdAt: docData.createdAt?.toDate(),
        updatedAt: docData.updatedAt?.toDate(),
      } as T;
    }),
    nextCursor: hasMore ? docs[pageSize - 1] : null,
    hasMore,
  };
};
